import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Tooltip from 'rc-tooltip';
import isEqual from 'lodash/isEqual';
import cx from 'classnames';
import OnImagesLoaded from 'react-on-images-loaded';

import Loader from '../common/loader';
import SxIcon from '../common/sxIcon';
import LanguageChanger from '../common/languageChanger';
import Page from '../page';
import SurveyNavigator from '../page/surveyNavigator';

import BlockStyle from './blockStyle';
import ConceptTestStyle from '../fields/conceptTest/conceptTestStyle';
import ConjointStyle from '../fields/conjoint/conjointStyle';
import ConstantSumStyle from '../fields/constantSum/constantSumStyle';
import VanWestendorpStyle from '../fields/vanWestendorp/vanWestendorpStyle';
import GaborGrangerStyle from '../fields/gaborGranger/gaborGrangerStyle';
import KeyDriverStyle from '../fields/keyDriver/keyDriverStyle';
import ContactInfoStyle from '../fields/contactInfo/contactInfoStyle';
import { ConjointExperimentStyle } from '../fields/conjoint2/conjointExperimentStyle';
import { VideoQuestionStyle } from '../fields/videoQuestion/videoQuestionStyle';
import DateStyle from '../fields/date/dateStyle';
import GridStyle from '../fields/grid/gridStyle';
import HeaderStyle from './headerStyle';
import HeatmapStyle from '../fields/heatmap/heatmapStyle';
import InputStyle from '../fields/input/inputStyle';
import MultipleStyle from '../fields/multipleChoice/multipleStyle';
import PageNavigatorStyle from '../page/pageNavigatorStyle';
import ProgressBarStyle from '../common/progressBarStyle';
import QuestionHeaderStyle from '../questionHeader/questionHeaderStyle';
import RankStyle from '../fields/rank/rankStyle';
import ScaleStyle from '../fields/scale/scaleStyle';
import SurveyStyle from './surveyStyle';
import WelcomePageStyle from '../common/welcomePageStyle';
import NumericMatrixStyle from '../fields/numericMatrix/NumericMatrixStyle';
import { EndOfPage } from './endOfPage';
import { LogoSize } from '../../store/slices/customization/customization';
import { isApollo, isMobile } from '../../utils/platform';
import { Features, isFeatureEnabled } from '../../featureToggles/features';

import { clearResponses, clearDependentQuestions } from '../../store/actions/responses';
import { clearClipboardPaste, setClipboardPasteToTrue } from '../../store/slices/clipboardPaste';
import { getVideoQuestionIsSubmissionDeclinedById, getVideoQuestionPermissionById, getVideoQuestionResponseById } from '../../store/slices/responses/selectors';

import { getTranslationOfSelectedLanguage } from '../utils';
import { shouldRenderMobile } from '../../utils/platform';
import { isVideoQuestion, striptagsAndUnescapeText } from '../utils/helpers';
import { PAGE_TYPES } from '../types/survey';
import { IMAGE_LOADING_TIMEOUT } from '../../constants';
import { isAppleDevice } from '../utils/utils';

import './survey.scss';

class Survey extends Component {
  constructor() {
    super();
    this.state = {
      padding: 0,
      endOfPage: false,
      agreeTerms: false,
      wereImagesLoaded: false,
    };
    this.isMobile = shouldRenderMobile();
    this.onChange = this.onChange.bind(this);
    this.trackScrolling = this.trackScrolling.bind(this);
    this.resetScrolling = this.resetScrolling.bind(this);
    this.onAgreeChange = this.onAgreeChange.bind(this);
    this.contentRef = React.createRef();
    this.onImagesLoaded = this.onImagesLoaded.bind(this);
    this.handleGlobalPaste = this.handleGlobalPaste.bind(this);
  }

  resetScrolling() {
    if (this.isMobile) {
      this.setState({
        endOfPage: true,
      });
    }
  }

  onAgreeChange(value) {
    this.setState({ agreeTerms: value });
  }

  trackScrolling() {
    const el = this.contentRef.current;
    if (this.isMobile && el) {
      if (document.body.getBoundingClientRect().bottom >= Math.floor(el.getBoundingClientRect().bottom)) {
        this.setState({
          endOfPage: true,
        });
        document.body.removeEventListener('scroll', this.trackScrolling);
        window.removeEventListener('resize', this.trackScrolling);
      }
    }
  }

  handleGlobalPaste() {
    this.props.setClipboardPasteToTrue();
  }

  componentWillUnmount() {
    if (this.isMobile) {
      document.body.removeEventListener('scroll', this.trackScrolling);
      window.removeEventListener('resize', this.trackScrolling);
    }
    document.body.removeEventListener('paste', this.handleGlobalPaste);
  }

  componentDidMount() {
    this.props.clearResponses();
    this.props.clearClipboardPaste();

    this.trackScrolling();
    if (this.isMobile) {
      document.body.addEventListener('scroll', this.trackScrolling);
    }
    document.body.addEventListener('paste', this.handleGlobalPaste);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      !isEqual(nextProps, this.props) ||
      this.state.endOfPage !== nextState.endOfPage ||
      this.state.agreeTerms !== nextState.agreeTerms ||
      this.state.wereImagesLoaded !== nextState.wereImagesLoaded
    );
  }

  onChange(frontendId, value, subtype) {
    this.props.onChangeItem(frontendId, value, subtype);
    this.props.clearDependentQuestions(frontendId);
  }

  getAlignStyle() {
    const { align = {} } = this.props.logo || {};
    switch (align) {
      case 'left':
        return { justifyContent: 'flex-start' };
      case 'center':
        return { justifyContent: 'center' };
      case 'right':
        return { justifyContent: 'flex-end' };
      default:
        return {};
    }
  }

  renderTestIndicator() {
    const { location: { search = '' } = {}, isPreview } = this.props;
    const isTest = search.substr(1) === 'test';
    if (!isTest) {
      return null;
    }
    // TODO: add missing icon
    return (
      <div className="testIndicator">
        Testing Link&nbsp;&nbsp;&nbsp;&nbsp;
        <Tooltip
          placement="bottom"
          trigger={['hover', 'click']}
          mouseEnterDelay={0.2}
          overlay={
            <span className="tooltip-content max-width min-width">
              This link can be used to test your project. Responses will not be recorded.
            </span>
          }
        >
          <SxIcon name="question" />
        </Tooltip>
      </div>
    );
  }

  renderBody() {
    const {
      prevPage,
      nextPage,
      isPrevPageEnabled,
      isNextPageEnabled,
      showNotification,
      pagingType,
      isCapture,
      locked,
    } = this.props;
    const isMobile = shouldRenderMobile();
    return (
      <div className="survey">
        <GridStyle />
        <div className={cx('questions-content', { mobile: isMobile })} ref={this.contentRef}>
          <Page
            onChange={this.onChange}
            prevPage={prevPage}
            nextPage={nextPage}
            isPrevPageEnabled={isPrevPageEnabled}
            isNextPageEnabled={isNextPageEnabled}
            showNotification={showNotification}
            pagingType={pagingType}
            isCapture={isCapture}
            onAgreeChange={this.onAgreeChange}
            resetScroll={this.resetScrolling}
            locked={locked}
          />
        </div>
      </div>
    );
  }

  renderLogoImage() {
    const logoAlignmentStyles = this.getAlignStyle();
    const { logo } = this.props;
    const wereImagesLoaded = this.state.wereImagesLoaded;
    if (logo && logo.url) {
      return (
        <div className={'logo-container'} style={logoAlignmentStyles}>
          <div
            className={cx('logo-wrapper', {
              hide: !wereImagesLoaded,
            })}
          >
            <img className="header-image" src={logo.url} alt={`${logo.url ? 'logo' : 'no logo'}`} />
          </div>
        </div>
      );
    }
    return null;
  }

  renderTitle() {
    const { showTitle } = this.props;
    if (showTitle) {
      return <div className="header-title" dangerouslySetInnerHTML={{ __html: this.props.title }} data-e2e-selector="project-title"/>;
    }
    return null;
  }

  onImagesLoaded() {
    this.setState({ wereImagesLoaded: true });
  }

  render() {
    const {
      fetching,
      customize,
      isPrevPageEnabled,
      isNextPageEnabled,
      submitResponse,
      isCapture,
      isWelcomePage,
      hasTermsAndConditions,
      useStickyNavigator,
      availableLanguages,
      dqa,
      isCurrentItemAVideoQuestion,
      hideNavigationBar,
      shouldHideElementsForVideoQuestion
    } = this.props;

    const { agreeTerms } = this.state;

    if (fetching) {
      return (
        <div className="surveyLoader">
          <Loader />
        </div>
      );
    }
    console.log('render survey');
    const isMobile = shouldRenderMobile();
    const disableNext = isWelcomePage && hasTermsAndConditions && !agreeTerms;
    const isApple = isAppleDevice(); // HSF
    const wereImagesLoaded = this.state.wereImagesLoaded;
    const { logo = {} } = this.props;
    const hasLogoSrc = logo.hasOwnProperty('url');
    return (
      <div className={cx('surveyContainer', 'background-container', { customizeType: customize, 'video-question-bg': isMobile && isCurrentItemAVideoQuestion })}>
        { !shouldHideElementsForVideoQuestion && <LanguageChanger />}
        <div className={cx('survey-wrapper', { noSticky: !useStickyNavigator, isMobile, isWelcomePage, isApple })}>
          <OnImagesLoaded
            onLoaded={this.onImagesLoaded}
            onTimeout={this.onImagesLoaded}
            timeout={IMAGE_LOADING_TIMEOUT}
          >
            <div className="survey-header">
              <div
                className={cx('logo-main-container', {
                  medium: logo.size === LogoSize.medium,
                  large: logo.size === LogoSize.large,
                  remove: !hasLogoSrc,
                })}
              >
                <div className={cx('loader', { ['remove']: wereImagesLoaded || !hasLogoSrc })}>
                  <Loader />
                </div>
                {this.renderLogoImage()}
              </div>
              {this.renderTestIndicator()}
              <div className="survey">
                <InputStyle />
                <ScaleStyle />
                <GridStyle />
                <ConstantSumStyle />
                <GaborGrangerStyle />
                <VanWestendorpStyle />
                <KeyDriverStyle/>
                <RankStyle />
                <BlockStyle />
                <HeaderStyle />
                <MultipleStyle />
                <DateStyle />
                <ContactInfoStyle />
                <ConceptTestStyle />
                <HeatmapStyle />
                <WelcomePageStyle />
                <QuestionHeaderStyle />
                <ProgressBarStyle />
                <SurveyStyle />
                <PageNavigatorStyle />
                <ConjointStyle />
                <NumericMatrixStyle />
                <ConjointExperimentStyle/>
                <VideoQuestionStyle/>
                {this.renderTitle()}
              </div>
            </div>
            <div className={cx({ ['loadingImages']: !wereImagesLoaded })}>
              <div className={cx('survey-body')}>{this.renderBody()}</div>
            </div>
          </OnImagesLoaded>
          <EndOfPage />
        </div>
        {(hideNavigationBar || shouldHideElementsForVideoQuestion)? null : (
          <SurveyNavigator
            prevPage={this.props.prevPage}
            nextPage={this.props.nextPage}
            isCapture={isCapture}
            submitResponse={submitResponse}
            isPrevPageEnabled={isPrevPageEnabled}
            isNextPageEnabled={isNextPageEnabled}
            disableNextFromWelcomePage={disableNext}
            dqa={dqa}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const {
    customization: projectCustomization,
    activeProject: {
      survey: { pageToShow, loading = false, projectTitle, projectId, isPreview, dqa } = {},
      pages = [],
      items = {}
    } = {},
    visualizationTypes: { isQuestionPreview = false } = {},
    capture: { availableLanguages = [], selectedLanguage = 'en' } = {},
  } = state;
  const page = pages[pageToShow] || {};
  const customization = projectCustomization || { question: {} };
  const isWelcomePage = page.type === PAGE_TYPES.WELCOME_PAGE;
  const hasTermsAndConditions =
    isWelcomePage && page.termsAndConditions && striptagsAndUnescapeText(page.termsAndConditions).trim() !== '';
  const translation = getTranslationOfSelectedLanguage(state, selectedLanguage);
  let title = projectTitle;
  if (translation.title) {
    title = translation.title;
  }
  const { showTitle, useStickyNavigator = true } = customization;
  const pageItems = page?.items || [];
  const itemId = pageItems[pageItems.length - 1];
  const item = Object.values(items).find((question) => question._id === itemId);
  const isCurrentItemAVideoQuestion = isFeatureEnabled(Features.videoQuestion) &&  isVideoQuestion(item);
  const isSubmissionDeclined = isVideoQuestion(item) && getVideoQuestionIsSubmissionDeclinedById(item._id)(state);
  const hasPermissionGranted = isCurrentItemAVideoQuestion && getVideoQuestionPermissionById(item._id)(state) === 'granted';
  const videoResponse = isCurrentItemAVideoQuestion ? getVideoQuestionResponseById(item._id)(state) ?? [] : [];
  const hasVideoRecorded = videoResponse.length > 0;
  const shouldHideElementsForVideoQuestion = hasPermissionGranted && !hasVideoRecorded && isMobile() && !isSubmissionDeclined;

  return {
    id: projectId,
    title,
    isPreview,
    fetching: loading,
    isWelcomePage,
    hasTermsAndConditions,
    showTitle,
    useStickyNavigator,
    availableLanguages,
    shouldHideElementsForVideoQuestion,
    hideNavigationBar: isQuestionPreview || props.hideNavigationBar,
    dqa: !isApollo() && dqa,
    isCurrentItemAVideoQuestion
  };
};

export const ConnectedSurvey = connect(mapStateToProps, { clearResponses, clearDependentQuestions, clearClipboardPaste, setClipboardPasteToTrue })(Survey);

export default withRouter(ConnectedSurvey);
